import React, { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Button, Grid, Icon, Popup } from "semantic-ui-react";
import ProgressiveTickCircle from "./aaiProgressiveTickCircle";
import VoiceDetailDisplay from "../utils/aaiVoiceDetailDisplay";
import SaveDiscardControls from "./aaiSaveDiscardControls";
import AaiContentTextBox from "../components/aaiContentTextBox";

import {
  pauseRecording,
  resumeRecording,
  startRecording,
  stopRecording,
  saveRecording,
  deleteRecording,
  initializeRecordingUtils,
  selectRecordingStatus,
  isAudioChunksEmpty,
  selectAudioChunksLength,
  selectTargetData,
  setSnippetDetails,
  setTargetData
} from "../slices/aaiRecordingUtilitySlice";

import micIcon from "../assets/recorderImc.png";
import "../styles.css";

/**
 * Recording controls component with simplified implementation to avoid infinite loops
 */
const RecordingControls = ({
  voiceDetailObj = {},
  contentText = '',
  hideControls = false,
  compact = false,
  onStatusChange = null // New callback prop for status changes
}) => {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.user.profile)?.data?.userId || 'unknown';

  const targetData = useSelector(selectTargetData);

  // Selectors for Redux state
  const recordingStatus = useSelector(selectRecordingStatus);
  const audioChunksLength = useSelector(selectAudioChunksLength);
  const audioChunksIsEmpty = useSelector(isAudioChunksEmpty);

  const {
    character: {
      character: { name } = { name: "Default Name" }
    } = {},
    variation = "Default Variation",
    textSnippet = { content: "Default text", textOverride: null },
  } = targetData?.character || {};

  // Initialize recording state only once when component mounts
  useEffect(() => {
    // Only initialize if we have the required data
    if (voiceDetailObj && contentText) {
      // Initialize recording state
      const targetData = {
        character: {
          character: {
            character: {
              uuid: voiceDetailObj.characterId || 'default-character',
              name: voiceDetailObj.characterItem?.name || 'Character'
            }
          },
          variation: voiceDetailObj.variationId || 'Default'
        },
        variation: voiceDetailObj.variationId || 'Default',
        textSnippet: {
          content: contentText
        }
      };

      // Set snippet details
      const snippetDetails = {
        details: {
          values: {
            literaryGroup: 'voice-actor-portfolios',
            litGroupBook: voiceDetailObj.portfolioId || 'default-portfolio'
          }
        },
        snippetValues: {
          values: {
            title: `${voiceDetailObj.characterItem?.name || 'Character'} - Inline Recording`,
            enabled: true
          }
        }
      };

      // Dispatch actions to set up recording state
      dispatch(setTargetData(targetData));
      dispatch(setSnippetDetails(snippetDetails));
    }
  }, []); // Empty dependency array to run only once

  // Notify parent component when recording status changes
  useEffect(() => {
    if (onStatusChange && (recordingStatus === 'completed' || recordingStatus === 'stopped')) {
      onStatusChange(recordingStatus, window.audioChunksRef);
    }
  }, [recordingStatus, onStatusChange]);

  // Get the content to display, prioritizing contentText prop, then textOverride, then content
  const getDisplayContent = useCallback(() => {
    // First priority: explicitly passed contentText prop
    if (contentText) {
      return contentText;
    }
    // Second priority: textOverride from targetData
    if (textSnippet?.textOverride?.textContent) {
      return textSnippet.textOverride.textContent;
    }
    // Third priority: content from targetData
    return textSnippet?.content || "";
  }, [contentText, textSnippet]);

  // Helper Functions for Popup Content
  const setPlayButtonPopupContent = useCallback(() => {
    if (recordingStatus === "recording" || recordingStatus === "paused") return "Stop Recording";
    return "Start Recording";
  }, [recordingStatus]);

  const setPauseResumePopupContent = useCallback(() => {
    return recordingStatus === "paused" ? "Resume Recording" : "Pause Recording";
  }, [recordingStatus]);

  // Event Handlers
  const handleStartStop = useCallback(() => {
    if (recordingStatus === "recording" || recordingStatus === "paused") {
      dispatch(stopRecording());
    } else {
      dispatch(startRecording());
    }
  }, [dispatch, recordingStatus]);

  const handlePauseResume = useCallback(() => {
    if (recordingStatus === "paused") {
      dispatch(resumeRecording());
    } else if (recordingStatus === "recording") {
      dispatch(pauseRecording());
    }
  }, [dispatch, recordingStatus]);

  const handleSave = useCallback(async () => {
    try {
      // Create a valid snippetDetails object with the required structure
      const customSnippetDetails = {
        details: {
          values: {
            literaryGroup: 'voice-actor-portfolios',
            litGroupBook: voiceDetailObj?.bookItem?.uuid || 'default-portfolio'
          }
        },
        snippetValues: {
          values: {
            title: `${voiceDetailObj?.characterItem?.name || 'Character'} - ${variation || 'Default'}`,
            enabled: true,
            editorContent: contentText || textSnippet?.content || ''
          }
        }
      };

      // Set the snippetDetails in the Redux state
      dispatch(setSnippetDetails(customSnippetDetails));

      // Initialize recording utils before saving to ensure all required data is available
      await dispatch(initializeRecordingUtils({ source: 'voiceActor' })).unwrap();

      // Proceed with saving, passing 'voiceActor' as the source and all necessary IDs
      dispatch(saveRecording({
        source: 'voiceActor',
        actorId: voiceDetailObj?.actorId,
        portfolioId: voiceDetailObj?.portfolioId,
        characterId: voiceDetailObj?.characterId,
        variationId: voiceDetailObj?.variationId
      }));
    } catch (error) {
      console.error("Error during save preparation:", error);
    }
  }, [dispatch, voiceDetailObj, variation, contentText, textSnippet]);

  const handleCancelReset = useCallback(() => {
    dispatch(deleteRecording());
  }, [dispatch]);

  return (
    <>
      <div
        style={{
          backgroundColor: "#9ac1c1",
          borderRadius: "5px",
          padding: "10px",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Grid>
          <Grid.Row className="noMargNoPad" verticalAlign="middle">
            {/* Microphone Icon */}
            <Grid.Column className="noMargNoPad" width={5}>
              <ProgressiveTickCircle
                radius={50}
                tickLength={15}
                totalTicks={24}
                tickDuration={1000}
                start={recordingStatus === "recording"}
                stop={
                  recordingStatus === "paused" || recordingStatus === "stopped"
                }
                clear={recordingStatus === "idle"}
                micIcon={micIcon}
              />
            </Grid.Column>

            {/* Start/Stop and Pause/Resume Buttons */}
            <Grid.Column width={6} textAlign="center">
              {/* Start/Stop Button */}
              <Popup
                content={setPlayButtonPopupContent()}
                trigger={
                  <Button
                    type="button"
                    circular
                    icon
                    color={
                      recordingStatus === "recording" ||
                        recordingStatus === "paused"
                        ? "red"
                        : "green"
                    }
                    onClick={handleStartStop}
                  >
                    <Icon
                      name={
                        recordingStatus === "recording" ||
                          recordingStatus === "paused"
                          ? "stop"
                          : "play"
                      }
                    />
                  </Button>
                }
              />

              {/* Pause/Resume Button */}
              <Popup
                content={setPauseResumePopupContent()}
                trigger={
                  <Button
                    type="button"
                    circular
                    icon
                    color="yellow"
                    disabled={
                      recordingStatus !== "recording" &&
                      recordingStatus !== "paused"
                    }
                    onClick={handlePauseResume}
                  >
                    <Icon
                      name={
                        recordingStatus === "paused" ? "play" : "pause"
                      }
                    />
                  </Button>
                }
              />
            </Grid.Column>

            {/* Cancel/Reset Button */}
            <Grid.Column className="noMargNoPad" width={4} textAlign="center">
              <Popup
                content="Cancel and Reset Recording"
                trigger={
                  <Button
                    type="button"
                    circular
                    icon
                    color="grey"
                    disabled={recordingStatus === "idle"}
                    onClick={handleCancelReset}
                  >
                    <Icon name="undo" />
                  </Button>
                }
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>

      {/* Voice Details and Content Text Box */}
      <Grid style={{ marginTop: "20px" }}>
        <Grid.Row>
          <Grid.Column>
            <VoiceDetailDisplay
              voiceDetailObj={voiceDetailObj}
              charSpeechData={targetData}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            <AaiContentTextBox
              key={textSnippet?.uuid}
              contentOverride={getDisplayContent()}
            />
          </Grid.Column>
        </Grid.Row>

        {/* Save/Discard Controls - only show if not hidden */}
        {!hideControls && (
          <SaveDiscardControls
            saveRecording={handleSave}
            discardRecording={handleCancelReset}
          />
        )}
      </Grid>
    </>
  );
};

export default React.memo(RecordingControls);
